import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useState, useMemo, useRef, useLayoutEffect } from 'react';
import {
  Stack,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import {
  CrossEntityDateCrudDataGrid,
  ValueWithEntityReference,
  CrossEntityDateCrudDataGridHandle,
  ValuePropConfig,
} from '../../CrossEntityDateCrudDataGrid';
import {
  DiagnosisIndicatorsReadDTO,
  DiagnosisReadDTO,
  MonthlyIndicatorValuesReadDTO,
  DiagnosisEnergyUseReadDTO,
} from '../../../generated/api-client';
import { DiagnosisStepHandle } from './Step.types';
import { apiClient } from '../../../services/apiClient';
import { EditIndicatorDialog } from './EditIndicatorDialog';
import {
  buildReferencePeriodYearlyValues,
  type SourceValueItem,
} from '../../../utils/referencePeriodYearlyValues';
import { useDiagnosisEnergyUseByDiagnosisId } from '../../../hooks';
import { GridRowId } from '@mui/x-data-grid';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { ActionSpacer } from '../../common';
import { AiSearchModal } from '../../search/AiSearchModal';
import { importAiItemToDiagnosis } from '../../../utils/aiDiagnosisImport';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import { formatNumberItalianSignificant } from '../../../utils/numberFormat';
import { LineChartWrapper } from '../../charts';

type ViewMode = 'table' | 'charts';

interface DiagnosisIndicatorsStepProps {
  diagnosisId?: string;
  diagnosis?: DiagnosisReadDTO | null;
}

export const DiagnosisIndicatorsStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisIndicatorsStepProps>(
    ({ diagnosisId, diagnosis }, ref) => {
      const queryClient = useQueryClient();
      const crossEntityGridRef = useRef<CrossEntityDateCrudDataGridHandle>(null);
      const crossEntityGridRefAnnual = useRef<CrossEntityDateCrudDataGridHandle>(null);
      const crossEntityGridRefPeriodoRiferimento = useRef<CrossEntityDateCrudDataGridHandle>(null);

      const [editDialogOpen, setEditDialogOpen] = useState(false);
      const [selectedIndicator, setSelectedIndicator] = useState<DiagnosisIndicatorsReadDTO | null>(
        null
      );
      const [deleteDialogState, setDeleteDialogState] = useState<{
        open: boolean;
        id: string | null;
      }>({ open: false, id: null });
      const [viewMode, setViewMode] = useState<ViewMode>('table');
      const [aiSearchOpen, setAiSearchOpen] = useState(false);

      const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, nextMode: ViewMode | null) => {
        if (nextMode) setViewMode(nextMode);
      };

      const queryKey = `diagnosis-indicators-${diagnosisId}`;
      const { data: indicators = [], isLoading: isLoadingIndicators } = useQuery<
        DiagnosisIndicatorsReadDTO[]
      >(
        queryKey,
        () =>
          apiClient.search_by_diagnosis_id_diagnosis_indicators_search_by_diagnosis_id__diagnosis_id__get(
            diagnosisId!
          ),
        { enabled: !!diagnosisId }
      );

      const indicatorIdsKey = useMemo(
        () =>
          indicators
            .map(i => i.id)
            .filter((id): id is string => !!id)
            .sort()
            .join(','),
        [indicators]
      );

      const calculatedValuesQueryKey = useMemo(
        () => ['all-indicator-values-calculated', diagnosisId, indicatorIdsKey],
        [diagnosisId, indicatorIdsKey]
      );

      // Carica gli usi energetici della diagnosi
      const { data: energyUses = [] } = useDiagnosisEnergyUseByDiagnosisId(diagnosisId);

      // Calcola i valori per tutti gli indicatori usando risolvi_formula
      const { data: crossEntityValues = [], isLoading: isLoadingIndicatorData } = useQuery<
        ValueWithEntityReference<MonthlyIndicatorValuesReadDTO>[]
      >(
        calculatedValuesQueryKey,
        async () => {
          if (!indicators.length) return [];
          // Calcola i dati per ogni indicatore usando risolvi_formula
          const allData: MonthlyIndicatorValuesReadDTO[] = [];
          for (const indicator of indicators) {
            if (indicator.id && indicator.formula) {
              try {
                // Chiama l'endpoint risolvi_formula per calcolare i valori mensili
                const calculatedValues =
                  await apiClient.risolvi_formula_diagnosis_indicators_risolvi_formula__indicator_id__get(
                    indicator.id
                  );
                // Converti i risultati in MonthlyIndicatorValuesReadDTO
                calculatedValues.forEach((cv: any) => {
                  allData.push({
                    id: undefined,
                    indicator_id: indicator.id,
                    year: cv.year,
                    month: cv.month,
                    value: cv.value,
                  } as MonthlyIndicatorValuesReadDTO);
                });
              } catch (error) {
                console.error(`Errore nel calcolo dell'indicatore ${indicator.id}:`, error);
                // In caso di errore, continua con gli altri indicatori
              }
            }
          }

          const a = allData.map((data, index) => {
            // Convert year/month to Date (month is 1-12, Date expects 0-11)
            const monthDate =
              data.year && data.month ? new Date(data.year, data.month - 1, 1) : new Date();

            return {
              value: data,
              entityId: data.indicator_id ?? '',
              month: monthDate,
            };
          });

          return a;
        },
        { enabled: !!diagnosisId && indicators.length > 0 }
      );

      // Valori annui per indicatore: media dei valori mensili per anno (periodo dati generati)
      const indicatorYearlyValues = useMemo(() => {
        if (!crossEntityValues.length) return [];
        const fromYear = diagnosis?.data_generated_from
          ? dayjs(diagnosis.data_generated_from).year()
          : new Date().getFullYear();
        const toYear = diagnosis?.data_generated_to
          ? dayjs(diagnosis.data_generated_to).year()
          : fromYear;
        const years = new Set<number>();
        for (let y = fromYear; y <= toYear; y++) years.add(y);

        const sumByEntityYear = new Map<string, number>();
        const countByEntityYear = new Map<string, number>();
        crossEntityValues.forEach(item => {
          const year = dayjs(item.month).year();
          if (!years.has(year)) return;
          const key = `${item.entityId}-${year}`;
          const num = Number((item.value as any)?.value ?? 0);
          sumByEntityYear.set(key, (sumByEntityYear.get(key) ?? 0) + num);
          countByEntityYear.set(key, (countByEntityYear.get(key) ?? 0) + 1);
        });

        const result: ValueWithEntityReference<{ value: number }>[] = [];
        indicators.forEach(ind => {
          const eid = ind.id;
          if (!eid) return;
          years.forEach(year => {
            const key = `${eid}-${year}`;
            const sum = sumByEntityYear.get(key) ?? 0;
            const count = countByEntityYear.get(key) ?? 0;
            const avg = count > 0 ? sum / count : 0;
            result.push({
              entityId: eid,
              date: new Date(year, 0, 1),
              value: { value: avg },
            });
          });
        });
        return result;
      }, [
        crossEntityValues,
        indicators,
        diagnosis?.data_generated_from,
        diagnosis?.data_generated_to,
      ]);

      // Totali per periodo di riferimento: solo mesi in [reference_period_from, reference_period_to]
      const indicatorReferencePeriodYearlyValues = useMemo((): ValueWithEntityReference<{
        value: number;
      }>[] => {
        if (!diagnosis?.reference_period_from || !diagnosis?.reference_period_to) return [];
        const entityIds = indicators.map(i => i.id).filter(Boolean) as string[];
        return buildReferencePeriodYearlyValues({
          sourceValues: crossEntityValues as SourceValueItem[],
          periodFrom: diagnosis.reference_period_from,
          periodTo: diagnosis.reference_period_to,
          entityIds,
          getValueToSum: item => Number((item.value as any)?.value ?? 0),
          buildValue: (_entityId, _year, sum, count, _ctx) => ({
            value: count > 0 ? sum / count : 0,
          }),
        });
      }, [
        crossEntityValues,
        indicators,
        diagnosis?.reference_period_from,
        diagnosis?.reference_period_to,
      ]);

      const indicatorYearlyValuesProps: ValuePropConfig<{ value: number }>[] = useMemo(
        () => [
          {
            field: 'value',
            label: 'Media annua',
            isPrimary: true,
            editable: false,
            getValue: v => (v != null ? Number((v as any).value) : null),
            setValue: v => v ?? { value: 0 },
          },
        ],
        []
      );

      // Posizione assoluta scroll orizzontale (valore unico per tutte e tre le tabelle)
      const [horizontalScrollLeft, setHorizontalScrollLeft] = React.useState(0);
      const setScrollPos = React.useCallback((pos: number) => {
        setHorizontalScrollLeft(prev => (prev === pos ? prev : pos));
      }, []);

      const deleteMutation = useMutation(
        async (id: string) => {
          await apiClient.delete_item_diagnosis_indicators__item_id__delete(id);
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(queryKey);
            toast.success('Indicatore eliminato con successo');
            setDeleteDialogState({ open: false, id: null });
          },
          onError: (error: any) => {
            toast.error(error?.message || "Eliminazione dell'indicatore non riuscita");
          },
        }
      );

      // I valori sono calcolati automaticamente, non c'è bisogno di salvarli
      React.useImperativeHandle(ref, () => ({
        save: async () => ({ success: true }),
        add: async () => {
          if (!diagnosisId) {
            toast.error('Salva prima i dati principali della diagnosi.');
            return;
          }
          // Apri il dialog di modifica con un nuovo indicatore
          setSelectedIndicator(null);
          setEditDialogOpen(true);
        },
        actions: ['save', 'add'],
      }));

      const handleEditClick = (id: GridRowId) => {
        const indicator = indicators.find(i => i.id === id);
        if (indicator) {
          setSelectedIndicator(indicator);
          setEditDialogOpen(true);
        }
      };

      const handleDeleteClick = (id: GridRowId) => {
        setDeleteDialogState({ open: true, id: id as string });
      };

      const handleCloseDeleteDialog = () => {
        setDeleteDialogState({ open: false, id: null });
      };

      const handleConfirmDelete = () => {
        if (deleteDialogState.id) {
          deleteMutation.mutate(deleteDialogState.id);
        }
      };

      // Callback quando una cella viene modificata
      const handleCellEditStop = () => {
        // Cell edit stopped
      };

      // Header personalizzato per la prima colonna (collapsed state)
      const dateColumnHeader = React.useMemo(() => {
        return (
          <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
            <Typography variant="caption" fontWeight={600}>
              Informazioni indicatori
            </Typography>
          </Stack>
        );
      }, []);

      // Header personalizzato per la prima colonna (expanded state)
      const dateColumnHeaderExpanded = React.useMemo(() => {
        return (
          <Stack spacing={0.5} sx={{ fontSize: '0.75rem' }}>
            <Typography variant="caption" fontWeight={600} sx={{ pt: '0.2rem' }}>
              Informazioni indicatori
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Codice
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Formula
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Unita di misura
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Uso energetico
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Correlazione R2
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              Valore baseline
            </Typography>
            <Box sx={{ height: '24px' }}></Box>
          </Stack>
        );
      }, []);

      // Header personalizzato per le colonne degli indicatori (collapsed state)
      const renderIndicatorHeader = React.useCallback(
        (
          indicator: DiagnosisIndicatorsReadDTO,
          monthlyValues: { month: Date; value: number | string | null }[]
        ) => {
          return (
            <Box sx={{ my: '4px' }}>
              <Typography
                variant="caption"
                fontWeight={600}
                noWrap
                title={indicator.code || ''}
                sx={{ textAlign: 'center' }}
              >
                {indicator.code || '-'}{' '}
                {indicator.udm_desiderata && `[${indicator.udm_desiderata}]`}
              </Typography>
            </Box>
          );
        },
        []
      );

      // Header personalizzato per le colonne degli indicatori (expanded state)
      const renderIndicatorHeaderExpanded = React.useCallback(
        (
          indicator: DiagnosisIndicatorsReadDTO,
          monthlyValues: { month: Date; value: number | string | null }[]
        ) => {
          return (
            <Stack spacing={0.5} sx={{ fontSize: '0.7rem', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleEditClick(indicator.id as any)}
                >
                  <EditIcon fontSize="small" />
                </Box>
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleDeleteClick(indicator.id as any)}
                >
                  <DeleteIcon fontSize="small" />
                </Box>
              </Box>
              <Typography variant="caption" fontWeight={600} noWrap>
                {indicator.code || '-'}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {indicator.formula || '-'}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {indicator.udm_desiderata || '-'}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {(() => {
                  const energyUseId = (indicator as any).energy_use_id;
                  if (!energyUseId) return '-';
                  const energyUse = energyUses.find(eu => eu.id === energyUseId);
                  return energyUse?.code || '-';
                })()}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {indicator.correlation_r2 || '-'}
              </Typography>
              <Typography variant="caption" fontWeight={400} noWrap>
                {indicator.reference_ipe || '-'}
              </Typography>
            </Stack>
          );
        },
        [handleEditClick, handleDeleteClick]
      );

      const hasGeneratedPeriod = diagnosis?.data_generated_from && diagnosis?.data_generated_to;
      const formatIndicatorValue = React.useCallback(
        (value: number | string | null | undefined) => {
          if (value == null || value === '') return '';
          const numericValue = typeof value === 'number' ? value : Number(value);
          if (!Number.isFinite(numericValue)) return value;
          return numericValue.toLocaleString('it-IT', {
            maximumSignificantDigits: DECIMAL_PLACES.indicatori_significant_digits,
          });
        },
        []
      );

      if (!diagnosisId) {
        return (
          <Typography color="text.secondary">
            Salva le informazioni della diagnosi per gestire gli indicatori.
          </Typography>
        );
      }

      if (!hasGeneratedPeriod) {
        return <Box sx={{ mt: 2 }}>Selezionare un periodo dati generali</Box>;
      }

      return (
        <>
          <Stack spacing={2} sx={{ p: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="h6">Indicatori</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  color="primary"
                  aria-label="Ricerca AI indicatori"
                  onClick={() => setAiSearchOpen(true)}
                  size="medium"
                >
                  <SearchIcon />
                </IconButton>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="table" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TableRowsIcon fontSize="small" />
                    <Typography variant="body2">Tabella</Typography>
                  </ToggleButton>
                  <ToggleButton value="charts" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <ShowChartIcon fontSize="small" />
                    <Typography variant="body2">Grafici</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
            {hasGeneratedPeriod && (
              <Stack spacing={3} sx={{ mt: 1 }}>
                <Box sx={{ width: '100%', display: viewMode === 'table' ? 'block' : 'none' }}>
                  <CrossEntityDateCrudDataGrid
                    ref={crossEntityGridRef}
                    periodFrom={diagnosis.data_generated_from!}
                    periodTo={diagnosis.data_generated_to!}
                    entities={indicators}
                    values={crossEntityValues}
                    getEntityLabel={indicator => indicator.formula || indicator.id || ''}
                    getEntitySortKey={indicator => (indicator as any).index ?? null}
                    getValue={valueObj => valueObj.value ?? null}
                    setValue={(valueObj, newValue) => {
                      const updated = { ...valueObj };
                      if (updated.value) {
                        updated.value = {
                          ...updated.value,
                          value:
                            newValue !== null && newValue !== '' ? Number(newValue) : undefined,
                        };
                      }
                      return updated as any;
                    }}
                    dateColumnHeader={dateColumnHeader}
                    dateColumnHeaderExpanded={dateColumnHeaderExpanded}
                    renderEntityHeader={renderIndicatorHeader}
                    renderEntityHeaderExpanded={renderIndicatorHeaderExpanded}
                    entityColumnWidth={120}
                    entityColumnWidthExpanded={200}
                    onCellEditStop={handleCellEditStop}
                    renderCell={({ value }) => formatIndicatorValue(value)}
                    height="calc(100vh - 380px)"
                    loading={isLoadingIndicators || isLoadingIndicatorData}
                    readOnly={true}
                    syncScrollLeft={horizontalScrollLeft}
                    onHorizontalScroll={setScrollPos}
                  />
                </Box>
                <Typography variant="subtitle1" sx={{ display: viewMode === 'table' ? 'block' : 'none' }}>
                  Dati di consumo energetico, di produzione e di costo - valori annui
                </Typography>
                <Box sx={{ width: '100%', display: viewMode === 'table' ? 'block' : 'none' }}>
                  <CrossEntityDateCrudDataGrid
                    ref={crossEntityGridRefAnnual}
                    granularity="year"
                    periodFrom={diagnosis.data_generated_from!}
                    periodTo={diagnosis.data_generated_to!}
                    entities={indicators}
                    values={indicatorYearlyValues}
                    valuesProps={indicatorYearlyValuesProps}
                    getEntityLabel={indicator => indicator.formula || indicator.id || ''}
                    getEntitySortKey={indicator => (indicator as any).index ?? null}
                    dateColumnHeader="Anno"
                    entityColumnWidth={120}
                    entityColumnWidthExpanded={200}
                    renderEntityHeader={renderIndicatorHeader}
                    renderCell={({ value }) => formatIndicatorValue(value)}
                    height={200}
                    loading={isLoadingIndicators || isLoadingIndicatorData}
                    readOnly={true}
                    disableHeaderExpand={true}
                    syncScrollLeft={horizontalScrollLeft}
                    onHorizontalScroll={setScrollPos}
                  />
                </Box>
                <Typography variant="subtitle1" sx={{ display: viewMode === 'table' ? 'block' : 'none' }}>
                  Periodo di riferimento per la diagnosi energetica
                </Typography>
                <Box sx={{ width: '100%', display: viewMode === 'table' ? 'block' : 'none' }}>
                  {diagnosis?.reference_period_from && diagnosis?.reference_period_to ? (
                    <CrossEntityDateCrudDataGrid
                      ref={crossEntityGridRefPeriodoRiferimento}
                      granularity="year"
                      periodFrom={diagnosis.reference_period_from}
                      periodTo={diagnosis.reference_period_to}
                      entities={indicators}
                      values={indicatorReferencePeriodYearlyValues}
                      valuesProps={indicatorYearlyValuesProps}
                      getEntityLabel={indicator => indicator.formula || indicator.id || ''}
                      getEntitySortKey={indicator => (indicator as any).index ?? null}
                      dateColumnHeader="Anno"
                      entityColumnWidth={120}
                      entityColumnWidthExpanded={200}
                      renderEntityHeader={renderIndicatorHeader}
                      renderCell={({ value }) => formatIndicatorValue(value)}
                      height={200}
                      loading={isLoadingIndicators || isLoadingIndicatorData}
                      readOnly={true}
                      disableHeaderExpand={true}
                      syncScrollLeft={horizontalScrollLeft}
                      onHorizontalScroll={setScrollPos}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Impostare il periodo di riferimento nello step Diagnosi.
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: viewMode === 'charts' ? 'block' : 'none' }}>
                  {isLoadingIndicators || isLoadingIndicatorData ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Caricamento indicatori...
                      </Typography>
                    </Stack>
                  ) : indicators.length === 0 ? (
                    <Typography color="text.secondary">Nessun indicatore definito.</Typography>
                  ) : (
                    <Stack spacing={4}>
                      {indicators.map(indicator => {
                        if (!indicator.id) return null;
                        const pts: MonthlyPoint[] = crossEntityValues
                          .filter(v => v.entityId === indicator.id && v.month != null)
                          .map(v => ({
                            month: dayjs(v.month as any).toDate(),
                            value: Number((v.value as any)?.value ?? NaN),
                          }))
                          .filter(p => Number.isFinite(p.value))
                          .sort((a, b) => a.month.getTime() - b.month.getTime());
                        return (
                          <IndicatorMonthlyChartBlock
                            key={indicator.id}
                            indicator={indicator}
                            points={pts}
                          />
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}
            <ActionSpacer></ActionSpacer>
          </Stack>

          <EditIndicatorDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedIndicator(null);
            }}
            indicator={selectedIndicator}
            diagnosisId={diagnosisId}
            existingIndicators={indicators}
          />

          <ConfirmDialog
            open={deleteDialogState.open}
            onCancel={handleCloseDeleteDialog}
            onConfirm={handleConfirmDelete}
            title="Conferma eliminazione"
            content="Sei sicuro di voler eliminare questo indicatore?"
            confirmText="Elimina"
            cancelText="Annulla"
            loading={deleteMutation.isLoading}
          />

          <AiSearchModal
            open={aiSearchOpen}
            onClose={() => setAiSearchOpen(false)}
            fixedEntityType="indicator"
            diagnosisId={diagnosisId}
            onInsert={async item => {
              if (!diagnosisId) return;
              await importAiItemToDiagnosis({
                diagnosisId,
                item,
                apiClient,
                queryClient,
                existingCodes: indicators
                  .map(i => (i.code || '').trim())
                  .filter(Boolean) as string[],
              });
              queryClient.invalidateQueries(queryKey);
              toast.success('Indicatore aggiunto alla diagnosi');
            }}
          />
        </>
      );
    }
  )
);

DiagnosisIndicatorsStep.displayName = 'DiagnosisIndicatorsStep';

type MonthlyPoint = { month: Date; value: number };

function useChartContainerWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** Stesso schema asse X di «Andamento consumi … e Produzione» (Consumi e costi). */
function buildMonthAxisAndSeries(points: MonthlyPoint[]): {
  xAxisData: Date[];
  data: (number | null)[];
} {
  const keys = Array.from(new Set(points.map(p => dayjs(p.month).format('YYYY-MM')))).sort();
  const xAxisData = keys.map(k => new Date(`${k}-01T12:00:00`));
  const data = keys.map(k => {
    const pt = points.find(p => dayjs(p.month).format('YYYY-MM') === k);
    return pt != null ? pt.value : null;
  });
  return { xAxisData, data };
}

interface IndicatorMonthlyChartBlockProps {
  indicator: DiagnosisIndicatorsReadDTO;
  points: MonthlyPoint[];
}

const IndicatorMonthlyChartBlock: React.FC<IndicatorMonthlyChartBlockProps> = ({
  indicator,
  points,
}) => {
  const { xAxisData, data } = useMemo(() => buildMonthAxisAndSeries(points), [points]);
  const titleCode = indicator.code || indicator.id || 'Indicatore';
  const udm = indicator.udm_desiderata?.trim();
  const seriesLabel = udm ? `${titleCode} (${udm})` : titleCode;
  const [chartContainerRef, containerWidth] = useChartContainerWidth();
  const chartWidth = useMemo(
    () => Math.max(containerWidth > 0 ? containerWidth : 600, xAxisData.length * 32),
    [containerWidth, xAxisData.length]
  );
  const bandAxisData = useMemo(
    () => xAxisData.map(v => (v instanceof Date ? v : String(v ?? ''))),
    [xAxisData]
  );
  const xAxisMonthStyle = xAxisData.length > 0 && xAxisData[0] instanceof Date;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        color="text.secondary"
        display="block"
        sx={{ mb: 1 }}
      >
        {titleCode}
        {udm ? (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.75 }}>
            [{udm}]
          </Typography>
        ) : null}
      </Typography>
      {!indicator.formula ? (
        <Typography variant="body2" color="text.secondary">
          Nessuna formula definita per questo indicatore.
        </Typography>
      ) : !points.length ? (
        <Typography variant="body2" color="text.secondary">
          Nessun dato mensile (periodo dati generali).
        </Typography>
      ) : (
        <Box ref={chartContainerRef} sx={{ width: '100%', minWidth: 0, overflow: 'auto' }}>
          <LineChartWrapper
            title={titleCode}
            width={chartWidth}
            height={480}
            margin={{ top: 40, right: 90, left: 90, bottom: xAxisMonthStyle ? 110 : 80 }}
            xAxis={[
              {
                data: bandAxisData as string[],
                scaleType: 'point',
                valueFormatter: (v: string | Date) =>
                  v instanceof Date
                    ? dayjs(v).locale('it').format('MMM YYYY')
                    : String(v ?? ''),
                ...(xAxisMonthStyle
                  ? {
                      tickSize: 8,
                      tickLabelStyle: {
                        angle: -45,
                        textAnchor: 'end',
                        dy: 14,
                      } as any,
                    }
                  : {}),
              },
            ]}
            yAxis={[
              {
                id: 'left',
                scaleType: 'linear',
                position: 'left',
                min: 0,
                valueFormatter: (v: number) =>
                  formatNumberItalianSignificant(v, DECIMAL_PLACES.indicatori_significant_digits),
              },
            ]}
            leftAxis="left"
            rightAxis={null}
            series={[
              {
                data: data.map(v => (v != null && !Number.isNaN(v) ? v : null)),
                label: seriesLabel,
                color: '#1976d2',
                showMark: !xAxisMonthStyle,
                connectNulls: false,
              },
            ]}
            slotProps={{
              legend: {
                direction: 'row',
                position: { vertical: 'bottom', horizontal: 'middle' },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
